// Figma: no frame provided yet — built using the established design system (bordered
// section shell, same as ../Esg/EsgSection.js) per the "extrapolate" decision.
// LOGIC PORTED from LC2's EngagementTab.js (commons/components/HomeTabs/Engagement/
// EngagementTab.js): same `getEngagementByAccountId` lazy fetch (communities param is
// 'all' for master/Administrator, else the user's permitted community sfids joined by
// ';' — same as MainLayout.js's communities fetch), same `engKey` switch (dashboard
// community sfid, or 'All communities' when no community is selected) picking the
// ['All time'] slice out of each response field, same `isAllEmpty` empty-state check
// (see ./utils.js).
// NOT PORTED (flagged, both live under Performance's chart-heavy territory which is
// explicitly out of scope for this pass — see the task's Performance exclusion):
//   - ClaimedRateInfo (LC2: commons/components/HomeTabs/Performance/ClaimedRateInfo.js),
//     shown only for `isStakeAdmin`, needs its own `getClaimedRateByAccount` endpoint +
//     ClaimedRateHeaderInfo component.
//   - ResidentsSpenging (LC2: commons/components/HomeTabs/Performance/Metrics/
//     ResidentsSpending.js), a chart component.
// EngagementFinancialUnlocks / EngagementStakeVisaStats also drop their recharts graphs —
// see each file's own header comment.
import React, {useEffect, useMemo, useState} from 'react';
import {useSelector} from 'react-redux';
import {Box, Stack, Typography} from '@mui/material';

import {useLazyGetEngagementByAccountIdQuery} from '../../../features/coreApi/metricsSlice';
import {getDashboardAccount, getDashboardCommunities, getDashboardCommunity} from '../../../app/store/reducers/dashboard';
import {getIsStakeAdmin, getTeamMemberRole} from '../../../app/store/reducers/session';
import {defaultEngState, isAllEmpty} from './utils';

import EngagementInsights from './EngagementInsights';
import EngagementFinancialUnlocks from './EngagementFinancialUnlocks';
import EngagementStatPanel from './EngagementStatPanel';
import EngagementStakeVisaStats from './EngagementStakeVisaStats';

const EngagementSection = () => {
    const [fetchEngagement, {data: response = {}, isLoading, isSuccess, isFetching, isError}] = useLazyGetEngagementByAccountIdQuery();

    const dashboardCommunity = useSelector(getDashboardCommunity);
    const dashboardAccount = useSelector(getDashboardAccount);
    const isStakeAdmin = useSelector(getIsStakeAdmin);
    const userRole = useSelector(getTeamMemberRole);
    const dashboardCommunities = useSelector(getDashboardCommunities);

    const [engKey, setEngKey] = useState('All communities');
    const [engData, setEngData] = useState(defaultEngState);

    useEffect(() => {
        if (dashboardAccount.sfid) {
            let communitiesAccess = 'all';
            if (userRole !== 'Administrator' && !isStakeAdmin) {
                communitiesAccess = dashboardCommunities.map((community) => community.sfid).join(';');
            }
            fetchEngagement({sfid: dashboardAccount.sfid, communities: communitiesAccess});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dashboardAccount, userRole, isStakeAdmin, dashboardCommunities]);

    useEffect(() => {
        if (dashboardCommunity.sfid && dashboardCommunity.sfid !== 'all') {
            setEngKey(dashboardCommunity.sfid);
        } else {
            setEngKey('All communities');
        }
    }, [dashboardCommunity]);

    useEffect(() => {
        if (isSuccess && !isLoading && !isFetching && engKey) {
            const obj = {};
            for (const part of Object.keys(defaultEngState)) {
                obj[part] = part in response ? response[part]?.[engKey]?.['All time'] || defaultEngState[part] : defaultEngState[part];
            }
            setEngData({...defaultEngState, ...obj});
        } else {
            setEngData(defaultEngState);
        }
    }, [isLoading, isSuccess, isFetching, engKey, response]);

    const {homeCreditBuilder, homeFinancialUnlocks, homeInsights, homeSVD, homeStakePay} = engData;

    const content = useMemo(() => {
        if (isLoading || isFetching) {
            return (
                <Typography variant="body2" color="text.secondary" sx={{textAlign: 'center', py: 4}}>
                    Loading engagement data…
                </Typography>
            );
        }
        if (isError) {
            return (
                <Typography variant="body2" color="error.main" sx={{textAlign: 'center', py: 4}}>
                    Failed to fetch engagement data.
                </Typography>
            );
        }
        if (isSuccess && isAllEmpty(engData)) {
            return (
                <Typography variant="body2" color="text.secondary" sx={{textAlign: 'center', py: 4}}>
                    No engagement data yet for this account.
                </Typography>
            );
        }
        if (isSuccess) {
            return (
                <Stack spacing={3}>
                    <EngagementInsights insights={homeInsights} />
                    <EngagementFinancialUnlocks financials={homeFinancialUnlocks} />
                    <Stack direction="row" spacing={3} alignItems="flex-start">
                        <Stack spacing={3} sx={{flex: 1}}>
                            <EngagementStatPanel info={homeStakePay} />
                            <EngagementStatPanel info={homeCreditBuilder} />
                        </Stack>
                        <EngagementStakeVisaStats stakeVisaDebitInfo={homeSVD} />
                    </Stack>
                </Stack>
            );
        }
        return null;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoading, isFetching, isError, isSuccess, engData]);

    return <Box>{content}</Box>;
};

export default EngagementSection;
